'use strict';

define('vb/private/stateManagement/scope',[
  'knockout',
  'vb/private/stateManagement/variable',
  'vb/private/stateManagement/stateUtils',
  'vb/private/stateManagement/context/withEnvContext',
  'vb/private/utils',
  'vb/private/constants',
  'vb/private/log',
  'vb/private/action/assignmentHelper',
  'vb/private/history',
  'vb/private/stateManagement/variableFactory',
], (ko, Variable, StateUtils, WithEnvContext, Utils, Constants, Log, AssignmentHelper, History, VariableFactory) => {
  const logger = Log.getLogger('/vb/scope');

  /**
   * @typedef {import('vb/private/stateManagement/container.js').default} Container
   */

  /**
   * @class Scope
   */
  class Scope {
    /**
     * A scope is used to manage the state of a collection variables and constants.
     * The scope defines the lifespan and the storage of a variable.
     * @param  {String} name         A string use to name the scope. This will be used by the storage
     * mechanism. The name can be made unique using the { unique: true } option.
     * @param  {Container} [container] the container object (page, flow, etc) that this scope is build for.
     * It can be null, like for action chain.
     * @param  {Object} [options={}]      An object with property describing how the scope should be built.
     * There are 2 options available:
     *   { unique: true } build a scope using a unique name, default is false.
     *   { silent: true } variables in the scope will not dispatch a change event
     */
    constructor(name, container, options = {}) {
      this.log = logger;

      if (options.unique === true) {
        this.name = `${name}/${Utils.generateUniqueId()}`;
      } else {
        this.name = name;
      }

      this._store = null; // Initialized in syncWithStore()

      this.variablesDef = [];
      this.trackedDirtyDataVariables = {};
      this.variableNamespaces = {};
      // namespace is one of 'variables, 'builtin', ...
      Object.values(Constants.VariableNamespace).forEach((namespace) => {
        this.variableNamespaces[namespace] = {};
        Object.defineProperty(this, namespace, {
          value: this.variableNamespaces[namespace],
          enumerable: true,
        });
      });

      this.silent = options.silent || false;

      // When creating a scope for a chain, container is null
      if (container) {
        // Used by getWebStorageItemName to generated an application specific storage key
        this.appId = container.application.definition.id;

        // store access to the container directly
        this.container = container;
      }

      // change listeners for persisted variables
      this.persistedVariablesListeners = [];

      // The object where sessionStorage and localStorage API exist
      // Having it defined as a property allows tests to inject a mock storage
      this.storageInterface = globalThis;

      if (container) {
        // Map this constants namespace to its scope so $withEnv can evaluate it with extra $env values.
        WithEnvContext.registerNamespace(this, this.variableNamespaces[Constants.VariableNamespace.CONSTANTS],
          Constants.VariableNamespace.CONSTANTS);
      }
    }

    /**
     * @param  {String} name         A string use to name the scope. This will be used by the storage
     * mechanism. The name can be made unique using the { unique: true } option.
     * @param  {Container} [container] the container object (page, flow, etc) that this scope is built for.
     * It can be null, like for action chain.
     * @param  {Object} [options]      An object with property describing how the scope should be built.
     * There are 2 options available:
     *   { unique: true } build a scope using a unique name, default is false.
     *   { silent: true } variables in the scope will not dispatch a change event
     */
    static createScope(name, container, options) {
      return new Scope(name, container, options);
    }

    /**
     * True if the store for this scope has already been initialized.
     * This is used to know if the new value should be the initialValue or in the store.
     * @type {Boolean}
     */
    get isStoreInitialized() {
      return !!this._store;
    }

    /**
     * Sets the dirty data tracking value of all variables being tracked for dirty data.
     * @param reset indicates whether the dirty data tracking variables need to have
     *              their dirty data state reset
     */
    initVarsMarkedForDirtyDataTracking(reset = false) {
      Object.keys(this.trackedDirtyDataVariables).forEach((dirtyDataVarName) => {
        const variable = this.getVariable(dirtyDataVarName);
        if (variable && (!reset || (reset && variable.isUpdated()))) {
          this.trackedDirtyDataVariables[dirtyDataVarName] = variable.getValueForDirtyData();
          variable.resetUpdatedState();
        }
      });
    }

    /**
     * Checks whether the current scope has any dirty data variables.
     * @return {boolean} true if there is at least one dirty data variable found;
     *                   false otherwise
     */
    checkForDirtyData() {
      // it's possible for the variable values to be updated back to their original values
      // so we need to compare the variable values between the latest state and
      // what we have in the dirty data state, if they are different than these variables
      // are truly dirty
      const isDirty = Object.keys(this.trackedDirtyDataVariables).find((dirtyDataVarName) => {
        const variable = this.getVariable(dirtyDataVarName);
        return variable && variable.isDirty(this.trackedDirtyDataVariables[dirtyDataVarName]);
      });

      return !!isDirty;
    }

    /**
     * Re-sets the dirty data state of variables being tracked for dirty data.
     */
    resetDirtyData() {
      this.initVarsMarkedForDirtyDataTracking(true);
    }

    /**
     * Re-sets the dirty data state of the given variable in the current scope.
     * @param variable the variable which dirty data state to reset
     */
    resetVariableDirtyData(variable) {
      this.trackedDirtyDataVariables[variable.name] = variable.getValueForDirtyData();
      variable.resetUpdatedState();
    }

    syncWithStore(store) {
      this._store = store;

      // When an extension is present, all the extension scopes are synced to the store
      // before the variables in the scope subscribe to the store
      if (!this.container || !this.container.extensions || Object.keys(this.container.extensions).length === 0) {
        this.initializeVariablesComputed();
      }
    }

    initializeVariablesComputed() {
      const { container } = this;
      if (container) {
        // Only if rules are enabled for the current container enforce special order of initializations, otherwise
        // just initialize using natural order in variablesDef.
        // container.package tells us container is in an appUi
        if (container.supportsRules(true) && container.package && !container.isExtension()) {
          // Enforce this order
          // - first all regular constants and variables are initialized
          // - then rules based interface constants are initialized
          // - finally all defaultValue based interface constants are initialized
          const { constants = {} } = container.definition.interface;
          // get all variables instances that are not interface constants taking care to not skip variables with the
          // same name. See VBS-42473
          const varsExcludingIntConstants = this.variablesDef.filter((vd) => !(Object.keys(constants).includes(vd.name)
            && vd.namespace === Constants.VariableNamespace.CONSTANTS));
          varsExcludingIntConstants.forEach((v) => v.getValue());

          const rulesEvaluatedConstants = this.variablesDef.filter((vd) => (vd.descriptor.useRules));
          if (rulesEvaluatedConstants.length) {
            // this runs rules and updates the values on all rules driven variables. Not all interface constants
            // will end up having a variable, for instance when a leaf customer extension overrides a constant and
            // sets a literal. See createConstant.
            container.getRulesEngine().getInitialValues();
          }

          // If leaf extension sets defaultValue expressions for one or more rules constant, then we get their values.
          const nonRulesEvaluatedConstants = this.variablesDef.filter((vd) => (vd.descriptor.useRules === false));
          nonRulesEvaluatedConstants.forEach((v) => v.getValue());
        } else {
          // For each variable, force an initial getValue to set up the observable and establish dependencies.
          // Since it's an observable, we don't worry about duplicate update.
          this.variablesDef.forEach((v) => v.getValue());
        }
      }
    }

    getState(variable) {
      return this._store.getState(variable);
    }

    updateState(variable, value) {
      if (this._store) {
        this._store.updateState(variable, value);
      }
    }

    /**
     * Create a variable in the scope.
     * If the variable already exist, nothing happens.
     *
     * @param  {String}  name       The name of the variable
     * @param  {String}  namespace  the namespace that this variable belongs to. Valid namespaces
     *                              are: state, data and metadata. A variable can only belong to one namespace.
     * @param  {*}  type            The type of the variable (see StateUtils.getType() for type details)
     * @param  {*}  defaultValue    The default value created from the variable definition (may
     *                              be a primitive, struct, or expr)
     * @param  {*}  inputParamValue Value passed in from the caller
     * @param  {Object} [descriptor] An object to defines the behavior of the variable.
     *                              The values can be:
     *                              { writable: Boolean } to create a read-only variable.
     *                              { persisted: String } to define the type of persistence to use.
     *                              The type is a string that can take the value "none", "device",
     *                              "session" or "history".
     *                              { rateLimit: { timeout: Number } } to specify the timeout in milliseconds for
     *                              { input: String } the input value for the variable descriptor ('fromUrl',
     *                              'fromCaller', ...) limiting how often onValueChanged should be fired.
     *                              { dependencies: Object}, a Map of dependencies (on other variables) for the current
     *                              variable. Contains 2 properties
     *                              expressions - Array of (variable) expressions that this variable configuration
     *                              references.
     *                              variables - Array of Objects representing the referenced variable info; where
     *                              each Object contains the following properties - scopeName, variableName. See
     *                              StateUtils.buildVariablesDependenciesGraph
     * @param  {Object}  [variableDef]
     * @return {Variable}           A Variable object
     */
    createVariable(name, namespace, type, defaultValue, inputParamValue, descriptor = {}, variableDef = {}) {
      let variable = this.getVariable(name, namespace);

      if (variable) {
        return variable;
      }

      // This scenario should not occur and trying to make it work is not performant
      if (this._store) {
        throw new Error(`Cannot create a variable ${name} because the scope ${this.name} has been added to the store.`);
      }

      let persisted;
      const fromUrl = descriptor.input === Constants.VariablePropertyInput.FROM_URL;
      const inputType = descriptor.persisted;
      if (inputType && inputType !== 'none') {
        persisted = inputType;
      }

      if (!namespace) {
        this.log.error('A namespace should be provided for the variable', name);
      } else if (this[namespace] === undefined) {
        this.log.error('The namespace', namespace, 'is not valid for the variable', name);
      }

      // Retrieve the initial value from the defaultValue, input parameter or persisted value
      // TODO: pavi: calculating initial value for type instances does not work
      //  - for instance factory types the initialValues for constructorParams must be determined using
      //  inputParamValue and persisted.
      //  - for extended types it's the same behavior as instance factory types. It may also be required to apply
      //  the new value on the sibling variables.
      //  - for type instances initialized with no constructors it's a no op
      const initialValue = this.calculateInitialValue(name, namespace, type, defaultValue, inputParamValue, persisted);

      variable = VariableFactory.createVariable(type, {
        scope: this,
        name,
        namespace,
        type,
        defaultValue,
        initialValue,
        descriptor,
        variableDef,
      });

      this.variablesDef.push(variable);

      // For persisted variables which value comes from input, store its initial value
      if (inputParamValue !== undefined && persisted) {
        try {
          this.storePersistedValue(namespace, name, persisted, variable.serialize(initialValue));
        } catch (error) {
          this.log.error(error);
        }
      }

      if (variable.isDirtyDataTrackingEnabled()) {
        this.trackedDirtyDataVariables[variable.name] = undefined;
      }

      // variable created under namespace
      Object.defineProperty(this.variableNamespaces[namespace], name,
        {
          get() {
            return variable.getValue();
          },
          set(value) {
            variable.setValue(value);
          },
          enumerable: true,
          configurable: true,
        });

      // Listen to variable change to update persisted storage
      if (persisted || fromUrl) {
        const varChangeTracker = {
          eventSource: variable.onValueChanged,
          eventListener: (payload) => {
            // only persist the value if there is a diff
            if (payload.diff) {
              if (persisted) {
                try {
                  const state = variable.serialize(payload.value);
                  this.storePersistedValue(payload.namespace, payload.name, persisted, state);
                } catch (error) {
                  this.log.error(error);
                }
              }
              if (fromUrl) {
                const defValue = Utils.resolveIfObservable(defaultValue);
                const { history } = this.container;
                let urlParamValue;
                let inputParameters = history.getInputParameters();

                if (payload.value !== defValue) {
                  inputParameters = inputParameters || {};
                  // Update input parameters so that they can be restored on the back button
                  inputParameters[payload.name] = payload.value;

                  urlParamValue = payload.value;
                } else if (inputParameters) {
                  // When the default value is restored, remove its value from history
                  delete inputParameters[payload.name];
                }

                history.setInputParameters(inputParameters);

                // When urlParamValue is undefined, the URL param is removed from the URL
                // This is the case when the new value is the default value.
                history.setUrlParameter(payload.name, urlParamValue);

                // Immediately update the URL but does not use History.sync() because
                // the cached URL is not yet complete, simply modify the current URL with new parameter
                const url = new URL(window.location.href);
                history.changeUrlParameter(url, payload.name, urlParamValue);
                Utils.changeBrowserState(history.getState(), url.href, 'replaceState');
              }
            }
          },
        };
        variable.addValueChangedListener(varChangeTracker.eventListener, this);
        this.persistedVariablesListeners.push(varChangeTracker);
      }

      return variable;
    }

    /**
     * Retrieve the initial value from the defaultValue, input parameter or persisted value
     * @param  {string} name       name of the variable
     * @param  {string} namespace  namespace for this variable
     * @param  {string} type       type
     * @param  {*} defaultValue    the default value
     * @param  {*} inputParamValue the input parameter
     * @param  {string|undefined} persisted  the persisted type, either "local", "session", "device" or undefined
     * @return {*}                 the initial value
     */
    calculateInitialValue(name, namespace, type, defaultValue, inputParamValue, persisted) {
      let initialValue;
      try {
        // input parameter value takes precedence over persisted value
        if (inputParamValue !== undefined) {
          initialValue = inputParamValue;
        } else if (persisted) {
          // Load the persisted value before creating the variable so that the initial value is set
          const persistedValue = this.loadPersistedValue(namespace, name, persisted);

          if (persistedValue !== undefined && persistedValue !== null) {
            initialValue = StateUtils.restorePersistedValue(persistedValue, defaultValue);
            if (this.log.isInfo) {
              this.log.info('Loaded', persisted, 'persisted', namespace,
                Utils.getWebStorageItemName(this.appId, this.name, namespace, name),
                'value:', initialValue);
            }
          }
        }

        // make sure the initialValue is coerced into the variable's type
        // Note: This code cannot be in StateUtils.createVariableInitialValue because it introduces a circular
        // require dependency between StateUtils and AssignmentHelper.
        if (initialValue !== undefined) {
          initialValue = AssignmentHelper.coerceType(initialValue, type);
        }

        initialValue = StateUtils.createVariableInitialValue(defaultValue, initialValue);
      } catch (error) {
        // Do not break when the variable cannot be loaded/parsed, just log the error
        this.log.error('Error calculating initial value of variable', name, 'of',
          this.container && this.container.fullPath, error);
      }

      return initialValue;
    }

    /**
     * Return the own property descriptors for the given variable.
     *
     * @param variable the variable from which to get the property descriptors
     * @returns {Object}
     */
    static getVariablePropertyDescriptors(variable) {
      if (typeof Object.getOwnPropertyDescriptors === 'function') {
        return Object.getOwnPropertyDescriptors(variable);
      }

      // IE11 - Object.getOwnPropertyDescriptors is not implemented in IE so we create the descriptors
      // by iterating over the property names returned from Object.getOwnPropertyNames.
      const descriptors = {};
      Object.getOwnPropertyNames(variable).forEach((name) => {
        descriptors[name] = Object.getOwnPropertyDescriptor(variable, name);
      });
      return descriptors;
    }

    /**
     * Adds a reference variable to the scope.
     * @param {Object} instance - The instance to create a reference variable for.
     * @param {Scope} context - The scope context where the referenced variable is defined.
     * @param {string} referenceVariableName - The name of the reference variable to create.
     * @param {string} namespace - The namespace where the reference variable will be created.
     * @returns {boolean} true if a reference variable was added, false otherwise.
     */
    addReferenceVariable(instance, context, referenceVariableName, namespace) {
      const { id } = instance;

      if (id && context) {
        const proxyTarget = instance[Constants.CHAIN_PROXY_TARGET];
        const variable = context.getVariable(id, Constants.VariableNamespace.VARIABLES);
        const value = variable && variable.getValue();

        // instance can be a proxy, so we need to check against the actual instance via
        // CHAIN_PROXY_TARGET symbol
        if (value === instance || value === proxyTarget) {
          // look up the instance properties variable whose name ends with _value
          const propVariable = context.getVariable(`${id}${Constants.BuiltinVariableName.VALUE}`,
            Constants.VariableNamespace.VARIABLES);

          if (propVariable) {
            // create a reference variable that points to the instance variable
            this.createReferenceVariable(referenceVariableName, namespace, variable);

            // create a reference variable that points to the instance properties variable
            this.createReferenceVariable(`${referenceVariableName}${Constants.BuiltinVariableName.VALUE}`,
              namespace, propVariable);

            return true;
          }
        }

        // caller-owned metadata descriptors do not have an associated _value variable, but they still need to be
        // treated as references so the receiving scope does not dispose the original metadata provider. Use the
        // metadata namespace for the source lookup; the namespace parameter is where the reference is created.
        const metadataVariable = context.getVariable(id, Constants.VariableNamespace.METADATA);
        const metadataValue = metadataVariable && metadataVariable.getValue();

        if (metadataVariable && (metadataValue === instance
          || (proxyTarget !== undefined && metadataValue === proxyTarget))) {
          this.createReferenceVariable(referenceVariableName, namespace, metadataVariable);
          return true;
        }
      }

      return false;
    }

    /**
     * Create and add a variable that references another variable.
     *
     * @param {string} variableName - The name of the reference variable.
     * @param {string} namespace - The namespace for the reference variable.
     * @param {Variable} referencedVariable - The variable being referenced.
     */
    createReferenceVariable(variableName, namespace, referencedVariable) {
      // create a shallow clone of the referenced variable
      // Note: Ideally, we would create a Proxy. However, since Proxy is not supported for IE11. we'll just create
      // a shallow clone.
      const referenceVariable = Object.create(
        Object.getPrototypeOf(referencedVariable),
        Scope.getVariablePropertyDescriptors(referencedVariable),
      );

      // set the name and namespace for the reference variable
      referenceVariable.name = variableName;
      referenceVariable.namespace = namespace;

      // create an empty dispose function so we don't actually dispose the referenced variable
      referenceVariable.dispose = () => {};

      // add the variable proxy to the scope
      this.variablesDef.push(referenceVariable);

      // variable created under namespace
      Object.defineProperty(this.variableNamespaces[namespace], variableName,
        {
          get() {
            return referencedVariable.getValue();
          },
          set(value) {
            referencedVariable.setValue(value);
          },
          enumerable: true,
          configurable: true,
        });
    }

    /**
     * Remove a variable in the scope. This is only used by test in scope2Spec.js
     * @param  {string} name        name of the variable to remove
     * @param  {string} namespace
     * @return {boolean}            true is a variable was removed
     */
    removeVariable(name, namespace = Constants.VariableNamespace.VARIABLES) {
      for (let i = 0; i < this.variablesDef.length; i += 1) {
        const variable = this.variablesDef[i];
        if (variable.name === name && variable.namespace === namespace) {
          this.variablesDef.splice(i, 1);

          if (this.variableNamespaces[namespace]) {
            delete this.variableNamespaces[namespace].name;
          } else {
            this.log.error('Unable to remove variable', name, 'in the namespace', namespace,
              'because this namespace does not exist');
          }

          if (variable.isDirtyDataTrackingEnabled()) {
            delete this.trackedDirtyDataVariables[name];
          }

          variable.dispose();

          return true;
        }
      }

      return false;
    }

    /**
     * Return a variable in the scope given its name.
     * @param  {string} name the name of the variable
     * @param  {string} namespace the namespace of the variable
     * @return {Variable}    a Variable object
     */
    getVariable(name, namespace = Constants.VariableNamespace.VARIABLES) {
      return this.variablesDef.find((variable) => (variable.name === name && variable.namespace === namespace));
    }

    /**
     * Retrieve the value of a variable from one of three storage type.
     * 1) session: value is stored on the browser sessionStorage.
     * 2) device: value is stored on browser localStorage
     * 3) history: value is stored on the browser history state.
     * If the type is not any of those storage type, undefine is returned.
     *
     * @param  {string} namespace    the namespace of the variable
     * @param  {string} variableName the name of the variable
     * @param  {string} type         the type of persistence, 'session','device' or 'history'.
     * @return {*}                   the stored value
     * @private
     */
    loadPersistedValue(namespace, variableName, type) {
      let webStorageType;
      switch (type) {
        case Constants.VariablePropertyPersisted.HISTORY: {
          const serializedValue = History.getVariable(this.name, namespace, variableName);
          let value;
          if (serializedValue) {
            value = JSON.parse(serializedValue);
          }
          return value;
        }
        case Constants.VariablePropertyPersisted.DEVICE: webStorageType = Constants.VariablePropertyPersisted.LOCAL;
          break;

        case Constants.VariablePropertyPersisted.LOCAL: // backward compatibility
        case Constants.VariablePropertyPersisted.SESSION: webStorageType = type;
          break;

        default:
          return undefined;
      }

      const newName = Utils.getWebStorageItemName(this.appId, this.name, namespace, variableName);
      // storage is either local or session
      const storage = `${webStorageType}Storage`;

      let value = this.storageInterface[storage].getItem(newName);
      // For backward compatibility, look if the value exist under the old name and
      // convert it to use the new name
      if (value === null || value === undefined) {
        const oldName = Utils.getWebStorageItemName('app', this.name, namespace, variableName);
        value = this.storageInterface[storage].getItem(oldName);
        if (value) {
          // Store the value using the new name
          this.storageInterface[storage].setItem(newName, value);
          // and remove the old one
          this.storageInterface[storage].removeItem(oldName);
        }
      }

      // getItem never returns undefined, so can safely call JSON.parse with its result
      return JSON.parse(value);
    }

    /**
     * Save the value of a variable on one of three storage type.
     * 1) session: value is stored on the browser sessionStorage.
     * 2) device: value is stored on browser localStorage
     * 3) history: value is stored on the browser history state.
     * If the type is not any of those storage type, nothing is saved.
     *
     * @param  {String} namespace        the namespace of the variable
     * @param  {String} variableName     the name of the variable
     * @param  {String} type             the type of persistence, 'session'. 'device' or 'history'
     * @param  {String} serializedValue  the serialized value to store
     * @private
     */
    storePersistedValue(namespace, variableName, type, serializedValue) {
      let webStorageType;

      switch (type) {
        case Constants.VariablePropertyPersisted.HISTORY: {
          History.setVariable(this.name, namespace, variableName, serializedValue);
          // Immediately update the history
          Utils.changeBrowserState(History.getState(), window.location.href, 'replaceState');
          return;
        }
        case Constants.VariablePropertyPersisted.DEVICE:
          webStorageType = Constants.VariablePropertyPersisted.LOCAL;
          break;

        case Constants.VariablePropertyPersisted.LOCAL: // backward compatibility
        case Constants.VariablePropertyPersisted.SESSION:
          webStorageType = type;
          break;

        default:
          return;
      }

      const itemName = Utils.getWebStorageItemName(this.appId, this.name, namespace, variableName);
      this.storageInterface[`${webStorageType}Storage`].setItem(itemName, serializedValue);
    }

    /**
     * Constants are stored directly in the scope, not in the store.
     * For maximum performance, constants are evaluated lazily and once evaluated,
     * they are redefined as readonly property.
     *
     * @param {string} name            the constant name
     * @param {string} namespace       the namespace that this constants belongs to.
     *                                 Valid namespaces are "constants" and "builtin"
     * @param {string} type            the type of the constant
     * @param {*}      value           the constant value (primitive or object or an expression func). Outside of
     *                                 primitives or literal object/array, the value is not yet evaluated
     *                                 is not yet resolved
     * @param {*}      inputParamValue the value from the input parameter
     * @param {string} [inputType]     either "none", "local", "device" or undefined
     * @param {Object} options         an object to specify the behavior of the constant.
     *                                 { freeze: false } specify if nested properties of the
     *                                 constant should be frozen (immutable). Default is true
     * @param  {Object} descriptor      An object that defines the behavior of the constant. The values can be:
     *                                  - valueFrom: "rules"
     *                                  - rulesExtension: <Extension> where rules are defined
     * @return {Variable}              A Variable object representing a constant if its defaultValue
     *                                 is a live expression, undefined otherwise
     */
    createConstant(name, namespace, type, value, inputParamValue, inputType,
      options = { freeze: true }, descriptor = {}) {
      let persisted;

      if (inputType && inputType !== 'none') {
        persisted = inputType;
      }

      if (namespace !== Constants.VariableNamespace.CONSTANTS
        && namespace !== Constants.VariableNamespace.BUILTIN) {
        // No need to throw an error. VB internal code controls what's the namespace is.
        return undefined;
      }

      const initialValue = this.calculateInitialValue(name, namespace, type, value, inputParamValue, persisted);
      const variable = new Variable(this, name, namespace, type, value, initialValue, descriptor);
      let getter;

      // this is to support constant with live expression.
      // Skip constants defined in a chain/action (when container is null)
      if (this.container && ko.isObservable(initialValue)) {
        // Create a variable to handle expression updates
        this.variablesDef.push(variable);

        // but only define a getter
        getter = () => variable.getValue();

        // variable created under namespace
        Object.defineProperty(this.variableNamespaces[namespace], name, {
          get: getter,
          enumerable: true,
          configurable: true,
        });

        return variable;
      }

      // Uses the lazy value pattern. The expression is evaluated only the first time
      // it is accessed then the getter become read-only with the finalValue.
      Object.defineProperty(this.variableNamespaces[namespace],
        name, {
          get() {
            // Evaluate any observable from expression in nested object properties and
            // deep freeze the object to enforce no changes
            const finalValue = Utils.deepResolve(initialValue, { freeze: options.freeze });

            // Once the final value is calculated, redefine the property as a value, so the
            // next time it is read, the value will be returned immediately.
            // Note that "this" here is this.variableNamespaces.constants
            Object.defineProperty(this, name, {
              value: finalValue,
              enumerable: true,
              configurable: true,
            });

            return finalValue;
          },
          configurable: true,
          enumerable: true,
        });

      // this is a constant with a non-live expression
      variable.onValueChanged = null;

      return variable;
    }

    /**
     * Called when a variable has been initialized and added to scope and is considered 'active', so subscribers can
     * be notified that it's safe to read the (variable) value
     * @param availableContexts
     * @return {Promise} resolves when all variables in this scope are activated
     */
    async activateVariables(availableContexts) {
      const { variablesDef } = this;
      if (variablesDef.length > 0) {
        this.log.info('Activate variables for scope', this.name);
        const promises = variablesDef.map((variable) => variable.activate(this, availableContexts));
        return Promise.all(promises);
      }

      return undefined;
    }

    dispose() {
      this.log.info('Disposing scope', this.name);

      this.variablesDef.forEach((variable) => {
        variable.dispose();
      });

      // Ideally we would remove the scope in the dispose of the variable but unfortunately all hell breaks loose
      // when variable of type MultiServiceDataProvider are disposed because of the way event listeners are
      // cleanup up.
      this.variablesDef.forEach((variable) => {
        // eslint-disable-next-line no-param-reassign
        variable.scope = undefined;
      });

      this.variablesDef = [];
      this.trackedDirtyDataVariables = {};
      Object.values(Constants.VariableNamespace).forEach((namespace) => {
        const obj = this.variableNamespaces[namespace];
        // Remove reference to variables/constants made in createVariable
        // eslint-disable-next-line guard-for-in, no-restricted-syntax
        for (const varName in obj) { delete obj[varName]; }
        this.variableNamespaces[namespace] = {};
      });

      // clean up persisted variables listeners
      this.persistedVariablesListeners.forEach((tracker) => {
        tracker.eventSource.remove(tracker.eventListener);
      });

      this.container = undefined;
      this._store = undefined;
    }
  }

  return Scope;
});

